import { z } from 'zod'
import {
  agentArtifactSchema,
  agentStatusSchema,
  agentStepSchema,
  assistantMessageErrorSchema,
  chatAttachmentSchema,
  chatEmailRefSchema,
  messageCitationSchema,
  messageUsageSchema,
} from './chat-events'

/**
 * Chat thread sync (PLAN-082). A thread's messages are rows in its ChatStore
 * Durable Object; the DO owns the thread socket. The server is the only
 * writer (procedures append user messages, the chat bridge streams the
 * assistant reply); the socket carries snapshots, deltas, status and typing.
 *
 * `ChatMessage` is the row as the SPA renders it (`use-chat-collab`).
 */

export const chatMessageSchema = z.object({
  id: z.string(),
  /** Monotonic position in the thread (also the FTS ordinal). */
  seq: z.number().int(),
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  threadId: z.string(),
  createdAt: z.string(),
  completedAt: z.string().optional(),
  userId: z.string().optional(),
  userName: z.string().optional(),
  userAvatarUrl: z.string().optional(),
  attachments: z.array(chatAttachmentSchema).optional(),
  email: chatEmailRefSchema.optional(),
  steps: z.array(agentStepSchema).optional(),
  artifacts: z.array(agentArtifactSchema).optional(),
  citations: z.array(messageCitationSchema).optional(),
  usage: messageUsageSchema.optional(),
  error: assistantMessageErrorSchema.optional(),
  deletedAt: z.string().optional(),
  deletedBy: z.string().optional(),
})
export type ChatMessage = z.infer<typeof chatMessageSchema>

export const chatTypingUserSchema = z.object({ userId: z.string(), name: z.string() })
export type ChatTypingUser = z.infer<typeof chatTypingUserSchema>

/** Messages in the first snapshot; older ones load on demand. */
export const CHAT_SNAPSHOT_LIMIT = 100

// ── Client → server ───────────────────────────────────────────────────────

export const chatClientMessageSchema = z.discriminatedUnion('t', [
  z.object({ t: z.literal('typing'), typing: z.boolean() }),
  /** Messages with seq < beforeSeq, newest first, at most `limit`. */
  z.object({
    t: z.literal('history'),
    beforeSeq: z.number().int(),
    limit: z.number().int().min(1).max(200).optional(),
  }),
])
export type ChatClientMessage = z.infer<typeof chatClientMessageSchema>

// ── Server → client ───────────────────────────────────────────────────────

export const chatServerMessageSchema = z.discriminatedUnion('t', [
  /**
   * The thread tail. Large tails arrive in chunks (`chunk.i` of `chunk.n`);
   * `agentStatus`, `typing`, `hasMore` and `self` ride on the first one.
   */
  z.object({
    t: z.literal('snapshot'),
    rev: z.number(),
    self: z.string().optional(),
    messages: z.array(chatMessageSchema),
    hasMore: z.boolean().optional(),
    agentStatus: agentStatusSchema.optional(),
    typing: z.array(chatTypingUserSchema).optional(),
    chunk: z.object({ i: z.number(), n: z.number() }),
  }),
  /** Reply to `history`. */
  z.object({
    t: z.literal('history'),
    messages: z.array(chatMessageSchema),
    hasMore: z.boolean(),
  }),
  /** A message was created or replaced wholesale. */
  z.object({ t: z.literal('message'), rev: z.number(), message: chatMessageSchema }),
  /**
   * Streaming update of an assistant message: `append` extends the text
   * (the common case), `content` replaces it; other fields replace wholesale.
   */
  z.object({
    t: z.literal('delta'),
    rev: z.number(),
    id: z.string(),
    append: z.string().optional(),
    content: z.string().optional(),
    steps: z.array(agentStepSchema).optional(),
    artifacts: z.array(agentArtifactSchema).optional(),
    citations: z.array(messageCitationSchema).optional(),
    usage: messageUsageSchema.optional(),
    error: assistantMessageErrorSchema.nullable().optional(),
    completedAt: z.string().optional(),
  }),
  z.object({ t: z.literal('status'), agentStatus: agentStatusSchema }),
  z.object({ t: z.literal('typing'), typing: z.array(chatTypingUserSchema) }),
  z.object({
    t: z.literal('deleted'),
    rev: z.number(),
    id: z.string(),
    /** Soft delete keeps the row (placeholder bubble); hard removes it. */
    mode: z.enum(['soft', 'hard']),
    deletedAt: z.string().optional(),
    deletedBy: z.string().optional(),
  }),
  z.object({ t: z.literal('error'), message: z.string() }),
])
export type ChatServerMessage = z.infer<typeof chatServerMessageSchema>
